// Module 6C.2 — Dynamic brand-kit theming engine.
// Reads `primary_color` + `typography_preset` from the current studio and
// injects CSS variables onto :root + loads the matching Google Font.
// Components already reference var(--brand-primary) etc., so they inherit
// the override automatically — no per-component changes needed.

const TYPOGRAPHY_URLS = {
  editorial: "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&family=Inter:wght@400;500;600;700&display=swap",
  modern:    "https://fonts.googleapis.com/css2?family=Manrope:wght@500;600;700;800&family=Inter:wght@400;500;600;700&display=swap",
  quiet:     "https://fonts.googleapis.com/css2?family=Lora:wght@500;600;700&family=Source+Sans+3:wght@400;500;600;700&display=swap",
};
const TYPOGRAPHY_STACKS = {
  editorial: { display: "'Playfair Display', Georgia, 'Times New Roman', serif", body: "'Inter', sans-serif" },
  modern:    { display: "'Manrope', 'Inter', sans-serif", body: "'Inter', sans-serif" },
  quiet:     { display: "'Lora', Georgia, serif", body: "'Source Sans 3', 'Inter', sans-serif" },
};

function hexToRgb(hex) {
  const h = hex.replace("#", "");
  return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16) };
}
function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  if (max === min) { h = s = 0; } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h /= 6;
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
}
function hslToHex(h, s, l) {
  s /= 100; l /= 100;
  const k = n => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = n => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const toHex = v => Math.round(255 * v).toString(16).padStart(2, "0");
  return "#" + toHex(f(0)) + toHex(f(8)) + toHex(f(4));
}

function ensureFontLink(url) {
  let link = document.getElementById("brand-kit-fonts");
  if (link && link.getAttribute("href") === url) return;
  if (link) link.remove();
  link = document.createElement("link");
  link.id = "brand-kit-fonts";
  link.rel = "stylesheet";
  link.href = url;
  document.head.appendChild(link);
}

export function applyBrandKit(studio) {
  if (!studio || typeof document === "undefined") return;
  const root = document.documentElement;

  const hex = (studio.primary_color || "#0e3030").toLowerCase();
  const { r, g, b } = hexToRgb(hex);
  const { h, s, l } = rgbToHsl(r, g, b);
  const hoverHex = hslToHex(h, s, Math.max(l - 8, 0));
  const paperSoft = hslToHex(h, Math.min(s, 12), 95);
  const border = hslToHex(h, Math.min(s, 8), 89);

  root.style.setProperty("--brand-primary", hex);
  root.style.setProperty("--brand-primary-hover", hoverHex);
  // Don't override --brand-paper / --brand-border globally for stu_nuptial
  // when colors are the default — preserves byte-identical look.
  // For non-default colors, the soft/border derivations apply.
  if (hex !== "#0e3030") {
    root.style.setProperty("--brand-paper", paperSoft);
    root.style.setProperty("--brand-border", border);
  } else {
    root.style.removeProperty("--brand-paper");
    root.style.removeProperty("--brand-border");
  }
  // Sync shadcn HSL primary
  root.style.setProperty("--primary", `${h.toFixed(0)} ${s.toFixed(0)}% ${l.toFixed(0)}%`);
  root.style.setProperty("--ring", `${h.toFixed(0)} ${s.toFixed(0)}% ${l.toFixed(0)}%`);

  const preset = studio.typography_preset || "editorial";
  const stacks = TYPOGRAPHY_STACKS[preset] || TYPOGRAPHY_STACKS.editorial;
  const url = TYPOGRAPHY_URLS[preset] || TYPOGRAPHY_URLS.editorial;
  ensureFontLink(url);
  root.style.setProperty("--font-display", stacks.display);
  root.style.setProperty("--font-body", stacks.body);
}

export function logoUrl(studio) {
  if (!studio || !studio.logo_updated_at) return null;
  return `/api/studios/${studio.id}/logo?v=${encodeURIComponent(studio.logo_updated_at)}`;
}
